import { useNewsCategoryLabels } from '@/context/newsCategoryLabelsContext';
import { getNewsCategoryLabel } from '@/util/storyblok/news-categories';
import { useEffect, useId, useRef, useState } from 'react';
import type { StoryData } from 'storyblok-js-client';
import type { ArticleHeaderBlokData } from 'components/organisms/article-header/ArticleHeader.data';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Image from 'components/atoms/image/Image';
import Heading from 'components/atoms/heading/Heading';
import PrimaryLink from 'components/atoms/primary-link/PrimaryLink';
import { useGlobalStore } from '../../../stores/global';
import Arrow from '@/assets/svg/arrow-right-light.svg';
import { formatDate } from 'util/dates.utils';
import { protocolRelativeToAbsoluteUrl, stripLocaleFromUrl } from 'util/url.utils';
import styles from './RelatedNewsCarousel.module.scss';

export type RelatedNewsProps = {
  title: string;
  items: StoryData<ArticleHeaderBlokData>[];
  previousLabel?: string;
  nextLabel?: string;
};

/** Uses the existing article model so curated or recent stories can share this view. */
export default function RelatedNewsCarousel({
  title,
  items,
  previousLabel = 'Previous articles',
  nextLabel = 'Next articles',
}: RelatedNewsProps) {
  const viewArticle = useGlobalStore().globalSettings.view_article || 'View article';
  const [activeCard, setActiveCard] = useState<string | null>(null);
  const { locale } = useRouter();
  const categoryLabels = useNewsCategoryLabels();
  const headingId = useId();
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const [bounds, setBounds] = useState({ start: true, end: true });

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const update = () => {
      const start = list.scrollLeft <= 1;
      const end = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
      setBounds((current) =>
        current.start === start && current.end === end ? current : { start, end },
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(list);
    list.addEventListener('scroll', update, { passive: true });
    return () => {
      observer.disconnect();
      list.removeEventListener('scroll', update);
    };
  }, [items]);

  const move = (direction: number) => {
    const list = listRef.current;
    if (!list) return;
    const card = list.firstElementChild;
    if (!card) return;
    const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(list).gap);
    list.scrollBy({
      left: direction * step,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  };

  if (!items.length) return null;

  return (
    <section className={styles.relatedNews} aria-labelledby={headingId}>
      <div className={styles.header}>
        <Heading
          id={headingId}
          as="h2"
          size="heading-03"
          transitionInType="fadeIn"
          disableTriggerInOnScroll={false}
          className={styles.heading}
        >
          {title}
        </Heading>
        {(!bounds.start || !bounds.end) && (
          <div className={styles.controls}>
            <button
              type="button"
              onClick={() => move(-1)}
              disabled={bounds.start}
              aria-label={previousLabel}
              aria-controls={listId}
              className={styles.previous}
            >
              <Arrow aria-hidden="true" focusable="false" />
            </button>
            <button
              type="button"
              onClick={() => move(1)}
              disabled={bounds.end}
              aria-label={nextLabel}
              aria-controls={listId}
            >
              <Arrow aria-hidden="true" focusable="false" />
            </button>
          </div>
        )}
      </div>
      <ul id={listId} ref={listRef} className={styles.cards}>
        {items.map(({ uuid, full_slug, content, published_at }) => {
          const image = content.key_image?.[0];
          const src = protocolRelativeToAbsoluteUrl(
            image?.image_landscape || image?.image_portrait || '',
          );
          const date = content.publish_date || published_at;
          return (
            <li key={uuid} className={styles.card}>
              <Link
                href={`/${stripLocaleFromUrl(full_slug, locale)}`}
                className={styles.link}
                onMouseEnter={() => setActiveCard(uuid)}
                onMouseLeave={() => setActiveCard(null)}
                onFocus={() => setActiveCard(uuid)}
                onBlur={() => setActiveCard(null)}
              >
                {src && (
                  <div className={styles.image}>
                    <Image
                      src={src}
                      srcDesktop={src}
                      alt={image?.alt_text || ''}
                      layout="fill"
                      className="abs-fill"
                      sizes="(min-width: 768px) 33vw, 85vw"
                      loading="lazy"
                    />
                  </div>
                )}
                <h3 className={styles.title}>{content.title}</h3>
                <div className={styles.footer}>
                  <div>
                    <p>{getNewsCategoryLabel(categoryLabels, content.category)}</p>
                    {date && (
                      <time dateTime={date.split(' ')[0]}>
                        {formatDate(date, 'D MMM YYYY', locale)}
                      </time>
                    )}
                  </div>
                  <PrimaryLink
                    forceTag="span"
                    label={viewArticle}
                    textVariant="body-standard"
                    className={styles.viewArticle}
                    disableTriggerInOnScroll
                    isVisible={{ onMount: false, value: activeCard === uuid }}
                    transitionInType="splitWordsSlideInUp"
                    transitionOutType="fadeOut"
                  />
                  <span className={styles.arrow} aria-hidden="true">
                    <Arrow focusable="false" />
                  </span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
